import * as React from 'react'
import cx from 'classnames'
import styles from './StarRating.sass'
import Icon, { IconName } from 'components/Icon'
import { FormGroupContainer } from 'components/inputs'

interface Props {
  value?: number
  max?: number
  error?: string
  iconName?: IconName
  iconSize?: number
  readOnly?: boolean
  disabled?: boolean
  onChange?: (value: number) => unknown
}

export default function StarRating({
  value = 0,
  onChange,
  error,
  max = 5,
  iconName = 'star',
  iconSize,
  disabled = false,
  readOnly = false,
}: Props) {
  const [valueBeingHoveredOn, setValueBeingHoveredOn] = React.useState(0)
  const createChangeHandler = (newValue: number) => () => {
    if (readOnly || disabled) {
      return
    }
    if (!onChange) {
      return
    }

    if (newValue !== value) {
      onChange(newValue)
    }
  }

  return (
    <FormGroupContainer error={error} className={styles.formContainer}>
      <div className={cx(styles.root)}>
        {Array.from({ length: max }).map((_, index) => {
          const starValue = index + 1
          return (
            <Star
              disabled={disabled}
              readOnly={readOnly}
              key={starValue}
              starValue={starValue}
              actualValue={value}
              iconName={iconName}
              iconSize={iconSize}
              valueBeingHoveredOn={valueBeingHoveredOn}
              onClick={createChangeHandler(starValue)}
              onHoverChange={(hovering) =>
                setValueBeingHoveredOn(
                  readOnly || disabled ? 0 : hovering ? starValue : 0
                )
              }
            ></Star>
          )
        })}
      </div>
    </FormGroupContainer>
  )
}

interface StarProps {
  starValue: number
  actualValue: number
  valueBeingHoveredOn: number
  disabled: boolean
  readOnly: boolean
  iconName: IconName
  iconSize?: number
  onClick(): unknown
  onHoverChange(hovering: boolean): unknown
}

function Star({
  starValue,
  actualValue,
  valueBeingHoveredOn,
  onClick,
  onHoverChange,
  iconName,
  iconSize,
  disabled,
  readOnly,
}: StarProps) {
  const click = React.useCallback(() => onClick(), [onClick])
  const hoverRef = React.useRef(null)

  const valueToCompareWith =
    valueBeingHoveredOn === 0 ? actualValue : valueBeingHoveredOn
  const filled = valueToCompareWith >= starValue
  const half =
    starValue === Math.ceil(valueToCompareWith) &&
    Math.ceil(valueToCompareWith) > valueToCompareWith

  return (
    <a
      className={cx(
        styles.star,
        disabled && styles.disabled,
        readOnly && styles.readOnly
      )}
      onClick={click}
      ref={hoverRef}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      style={{ fontSize: iconSize }}
      data-test={`star-value-${starValue}`}
    >
      <span className={styles.under}>
        <Icon auto name={iconName} />
      </span>
      <span
        className={styles.over}
        style={{ width: half ? '50%' : filled ? '100%' : '0%' }}
      >
        <Icon auto name={iconName} />
      </span>
    </a>
  )
}
